import { useMemo } from "react";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  GetTicketssDocument,
  GetTicketsDocument,
  CreateTicketsDocument,
  UpdateTicketsDocument,
  DeleteTicketsDocument,
  GetTicketsQuery,
  GetTicketssQuery,
  CreateTicketsMutation,
  UpdateTicketsMutation,
  DeleteTicketsMutation,
  Tickets,
  TicketsInsertInput,
  TicketsUpdateInput,
} from "@/src/gql/graphql";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
import type { SimpleFieldConfig } from "@/lib/types/table/field-config-types";
import { getEntityTableConfig } from "@/lib/entity-registry";
import { makeTicketTemplateHooks, type TicketTemplateHooks } from "./ticket-template-adapter-hooks";
import { usePaginatedCollection } from "@/lib/hooks/api/shared/use-paginated-collection";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import {
  GetTicketssClientDocument,
  GetTicketsClientDocument,
} from "./client-safe-ticket-query";

// Lazily resolved (NOT at module top-level): calling getEntityTableConfig during
// this module's evaluation forms a require cycle with `@/lib/entity-registry`
// under CommonJS/jest, leaving it `undefined`. Deferring to first use runs it
// after all modules initialize. See TESTING_SYSTEMS.
let _ticketConfig: ReturnType<typeof getEntityTableConfig<Tickets>> | undefined;
const ticketConfig = (): ReturnType<typeof getEntityTableConfig<Tickets>> =>
  (_ticketConfig ??= getEntityTableConfig<Tickets>("tickets"));

export type { TicketTemplateHooks };

const withTicketNodeId = <T extends { id: string }>(ticket: T): T & { nodeId: string } => ({
  ...ticket,
  nodeId: ticket.id,
});

export const useTicketHooks = (): EntityHooks<Tickets> & TicketTemplateHooks => {
  // SECURITY: client_user reads must omit internal/staff-only ticket columns.
  // Fail-closed — anyone who is not a confirmed employee (incl. role still
  // loading) gets the pruned, client-safe documents. See USER_ROLES_AND_ACCESS
  // / CLIENT_USER_ACCESS_SCOPE and client-safe-ticket-query.ts.
  const { isEmployee } = useRoleCheck();
  const clientSafe = !isEmployee;
  const listDocument = clientSafe ? GetTicketssClientDocument : GetTicketssDocument;
  const singleDocument = clientSafe ? GetTicketsClientDocument : GetTicketsDocument;

  return useMemo(
    (): EntityHooks<Tickets> & TicketTemplateHooks => ({
      queryDocument: listDocument,

      usePaginated: (params?: Record<string, unknown>) =>
        // Canonical list-query path (cache-and-network) — see use-paginated-collection.ts.
        usePaginatedCollection(
          listDocument,
          params,
          (data: GetTicketssQuery) => data.ticketsCollection,
          (node) => withTicketNodeId(node) as Tickets
        ),

      useInfinite: (_params?: Record<string, unknown>) => ({
        data: undefined,
        isLoading: false,
        error: null,
        refetch: () => {},
        hasNextPage: false,
        isFetchingNextPage: false,
        fetchNextPage: () => {},
      }),

      useSingle: (id: string, options?: { enabled?: boolean }) => {
        const result = useQuery<GetTicketsQuery>(singleDocument, {
          variables: { id },
          errorPolicy: "all",
          skip: !id || (options && options.enabled === false),
        });
        return {
          ...result,
          data: result.data?.ticketsCollection?.edges?.[0]?.node
            ? (withTicketNodeId(result.data.ticketsCollection.edges[0].node))
            : undefined,
          isLoading: result.loading,
          error: result.error || null,
          refetch: result.refetch,
        };
      },

      useUpdate: () => {
        const [mutate, result] = useMutation<UpdateTicketsMutation>(UpdateTicketsDocument);
        const TICKETS_UPDATE_KEYS = new Set([
          "actual_hours", "assigned_to", "auto_assign", "auto_escalate", "billable_hours",
          "category", "closed_at", "company_id", "company_name", "contact_id", "created_at",
          "created_by", "customer_visible_notes", "description", "discount_amount",
          "discount_percentage", "discount_type", "due_date", "escalation_level",
          "estimated_hours", "has_time_entries", "hourly_rate", "internal_notes",
          "is_internal", "last_activity_at", "last_escalation_at", "non_billable_hours",
          "priority", "reminder_sent", "reminder_sent_at", "resolved_at", "sla_hours",
          "status", "tags", "ticket_number", "title", "total_billed", "total_cost",
          "updated_at",
        ]);
        return {
          mutateAsync: async ({ id, ...input }: { id: string } & TicketsUpdateInput) => {
            const cleanInput: Record<string, unknown> = {};
            for (const key of Object.keys(input)) {
              if (TICKETS_UPDATE_KEYS.has(key) && input[key as keyof typeof input] !== undefined) {
                cleanInput[key] = input[key as keyof typeof input];
              }
            }

            const integerFields = ["sla_hours", "escalation_level"];
            integerFields.forEach((field) => {
              if (field in cleanInput && cleanInput[field] !== null && cleanInput[field] !== undefined) {
                const numValue = Number(cleanInput[field]);
                if (!isNaN(numValue)) cleanInput[field] = numValue;
              }
            });

            const { data, error } = await mutate({
              variables: { id, input: cleanInput },
              errorPolicy: "all",
              refetchQueries: ["GetTicketss", "GetTickets"],
              awaitRefetchQueries: true,
            });
            if (error) throw new Error(`GraphQL error: ${error.message}`);
            const updated = data?.updateticketsCollection?.records?.[0];
            if (!updated) throw new Error("Failed to update ticket");
            return withTicketNodeId(updated);
          },
          isPending: result.loading,
        };
      },

      useDelete: () => {
        const [mutate, result] = useMutation<DeleteTicketsMutation>(DeleteTicketsDocument);
        return {
          mutateAsync: async (id: string) => {
            const { data, error } = await mutate({
              variables: { id },
              errorPolicy: "all",
              refetchQueries: ["GetTicketss"],
              awaitRefetchQueries: true,
            });
            if (error) throw new Error(`GraphQL error: ${error.message}`);
            const deletedId = data?.deleteFromticketsCollection?.records?.[0]?.id;
            return deletedId || id;
          },
          isPending: result.loading,
        };
      },

      useCreate: () => {
        const [mutate, result] = useMutation<CreateTicketsMutation>(CreateTicketsDocument);
        return {
          mutateAsync: async (input: TicketsInsertInput) => {
            const titleTrim = typeof input.title === "string" ? input.title.trim() : "";
            const descTrim = typeof input.description === "string" ? input.description.trim() : "";
            if (!titleTrim || !descTrim) {
              throw new Error("Title and description are required.");
            }

            const { __typename: _typename, id: _id, ...cleanInput } = input as TicketsInsertInput & {
              __typename?: unknown;
            };

            if (cleanInput.contact_id === "") cleanInput.contact_id = undefined;
            if (cleanInput.assigned_to === "") cleanInput.assigned_to = undefined;
            if (cleanInput.created_by === "") cleanInput.created_by = undefined;

            const integerFields = ["sla_hours", "escalation_level"];
            integerFields.forEach((field) => {
              if (field in cleanInput && cleanInput[field as keyof typeof cleanInput] !== null && cleanInput[field as keyof typeof cleanInput] !== undefined) {
                const numValue = Number(cleanInput[field as keyof typeof cleanInput]);
                if (!isNaN(numValue)) {
                  cleanInput[field as keyof typeof cleanInput] = numValue;
                }
              }
            });

            const { data, error } = await mutate({
              variables: { input: cleanInput },
              errorPolicy: "all",
              refetchQueries: ["GetTicketss"],
              awaitRefetchQueries: true,
            });
            if (error) throw new Error(`GraphQL error: ${error.message}`);
            const created = data?.insertIntoticketsCollection?.records?.[0];
            if (!created) throw new Error("Failed to create ticket");

            if (created.contact_id) {
              try {
                await fetch("/api/tickets/webhook", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(created),
                });
              } catch (webhookError) {
                console.error("Failed to send Slack notification:", webhookError);
              }
            }

            return withTicketNodeId(created);
          },
          isPending: result.loading,
        };
      },

      getTableConfig: () => ticketConfig(),

      getFieldConfig: (fieldName: string): SimpleFieldConfig | null | undefined => {
        return (ticketConfig().fields[fieldName]) || null;
      },

      ...makeTicketTemplateHooks(),
    }),
    [listDocument, singleDocument]
  );
};
